/**
 * Hand the anchor to the native watch, and keep telling it we are still here.
 *
 * Edd ran the watch overnight, lifted the anchor in the morning, drove off, and
 * nothing sounded. Everything visible said it was working: the blue location icon
 * was up and the service notification still said the watch was on. Both were
 * true. The native location service really was alive and delivering fixes the
 * whole time, and the thing comparing those fixes with the anchor was JavaScript
 * in a WebView that Android had taken away hours earlier. Fixes arriving with
 * nobody reading them is the worst shape this could take, because there is no
 * outward sign of it at all.
 *
 * So the anchor now goes to Java as well (AnchorWatch.java), which keeps checking
 * whether or not this code is running. Two independent readers of the same fixes.
 *
 * `alive()` is what stops them both alarming at once. While this side is running
 * its own check it stamps a file, and the native side stays quiet and lets the
 * on-screen alarm have it, because that one comes with the chart. When the stamps
 * stop, native takes over. It does not ask why they stopped, which is the point:
 * a backup that shares an assumption with the thing it is backing up is not a
 * backup.
 *
 * Nothing here throws or rejects. This is the belt to the web app's braces, and a
 * backup that can take the primary down with it is worse than no backup.
 */
import { registerPlugin, Capacitor } from '@capacitor/core';

export interface NativeAnchorArm {
  lat: number;
  lon: number;
  /** Swing circle plus the GPS-wander buffer, in metres. The whole boundary. */
  radiusM: number;
  mode: 'radius' | 'zone';
  /** [lon, lat] ring, used when mode is 'zone'. */
  zone: [number, number][];
  muted: boolean;
}

export interface NativeAnchorStatus {
  armed?: boolean;
  /** How long since the native check last saw a fix, ms. -1 when it never has. */
  lastCheckAgeMs?: number;
  log?: string;
  error?: string;
}

interface AnchorWatchNativePlugin {
  arm(o: NativeAnchorArm): Promise<{ error?: string }>;
  disarm(): Promise<{ error?: string }>;
  alive(): Promise<{ error?: string }>;
  status(): Promise<NativeAnchorStatus>;
  /** Pre-serialised snapshots: the two sides share bytes, not a schema. */
  routeWatch(o: { json: string }): Promise<{ error?: string }>;
  aisSnapshot(o: { json: string }): Promise<{ error?: string }>;
  /** Arm-time question: would an alarm fired right now actually be heard. */
  checkAlarmDelivery(): Promise<{ problem?: string; kind?: string }>;
  openNotificationSettings(): Promise<{ error?: string }>;
  requestUnrestrictedBattery(): Promise<{ error?: string }>;
}

/** What the arm-time check found, and which Android setting fixes it. */
export interface AlarmDeliveryProblem {
  problem: string;
  kind: 'notifications' | 'battery';
}

const AnchorWatchNative = registerPlugin<AnchorWatchNativePlugin>('AnchorWatchNative');

/**
 * How often to stamp.
 *
 * Fixes arrive every few seconds and each stamp is a bridge call that writes a
 * file, so stamping on every one would be a lot of work to say something that
 * changes slowly. The native side waits ninety seconds before taking over, so
 * twenty gives us four missed stamps of margin before it decides we are gone.
 */
export const ALIVE_STAMP_MS = 20_000;

let lastStampMs = 0;
/** The last thing sent, so an unchanged anchor is not re-sent on every render. */
let lastArmKey: string | null = null;

function armKey(a: NativeAnchorArm): string {
  return [
    a.lat.toFixed(6),
    a.lon.toFixed(6),
    Math.round(a.radiusM),
    a.mode,
    a.muted ? 'm' : '-',
    a.zone.length,
    // The ring itself, so reshaping a drawn zone reaches the native side. Rounded
    // to about a metre, which is finer than anyone can drag a boundary handle.
    a.zone.map((p) => `${p[0].toFixed(5)},${p[1].toFixed(5)}`).join(';'),
  ].join('|');
}

/** Tell the native watch what to watch. Sends only when something changed. */
export async function armNativeAnchor(a: NativeAnchorArm): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const key = armKey(a);
  if (key === lastArmKey) return;
  try {
    await AnchorWatchNative.arm(a);
    lastArmKey = key;
    lastSentDisarmed = false;
  } catch {
    // The web app's own check is still running. Losing the backup is worth
    // knowing about, but not worth interrupting anybody over.
  }
}

/** Whether the last thing we told the native side was "disarmed", so the
 * runner's effect re-running on unrelated dep changes does not write a fresh
 * "disarmed by the app" log line every time (five in five seconds on 10 Aug,
 * eating the black box's forensic space). */
let lastSentDisarmed = false;

/** Nothing is anchored any more. Stops the native watch and its watchdog. */
export async function disarmNativeAnchor(): Promise<void> {
  lastArmKey = null;
  lastStampMs = 0;
  if (!Capacitor.isNativePlatform()) return;
  if (lastSentDisarmed) return;
  lastSentDisarmed = true;
  try {
    await AnchorWatchNative.disarm();
  } catch {
    // A watch that stays armed natively when it should not costs a false alarm
    // at worst, and the native side re-reads the armed state on every fix, so
    // the next successful disarm clears it.
  }
}

/** "Still here, I am doing the checking." Throttled; call it on every fix. */
export async function stampAlive(nowMs = Date.now()): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (nowMs - lastStampMs < ALIVE_STAMP_MS) return;
  lastStampMs = nowMs;
  try {
    await AnchorWatchNative.alive();
  } catch {
    // A missed stamp makes the native side take the alarm, which is the safe
    // direction to fail in.
  }
}

/**
 * The last thing the native watch said about itself.
 *
 * Cached because the bug report is built synchronously and asking across the
 * bridge is not, which is the same reason the renderer-death log is read once and
 * kept. Refreshed on a slow timer from the fix path and whenever the Diagnostics
 * panel is opened, so what gets sent is minutes old at worst.
 */
let cachedLine: string | null = null;

/** What the native watch has been doing, for the Diagnostics panel. */
export async function nativeAnchorStatus(): Promise<NativeAnchorStatus | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const s = await AnchorWatchNative.status();
    cachedLine = describeNativeStatus(s);
    return s;
  } catch {
    return null;
  }
}

/** One line for a report, or null when there is nothing native to say. */
export function nativeAnchorLine(): string | null {
  return cachedLine;
}

export function describeNativeStatus(s: NativeAnchorStatus): string {
  const ms = s.lastCheckAgeMs;
  const age =
    ms == null || ms < 0
      ? null
      : Math.round(ms / 1000) < 120
        ? `${Math.round(ms / 1000)} s ago`
        : Math.round(ms / 1000) < 7200
          ? `${Math.round(ms / 60000)} min ago`
          : `${Math.round(ms / 3600000)} h ago`;
  if (!s.armed) {
    // The stamp age still matters with nothing armed: on 9 Aug the one question
    // the dump could not answer was WHEN the native watch last checked anything,
    // because this line hid the age behind "nothing armed".
    return age == null ? 'nothing armed' : `nothing armed (native last checked a fix ${age})`;
  }
  if (age == null) return 'ARMED, but it has never checked a fix';
  return `ARMED, last checked a fix ${age}`;
}

/**
 * How often to re-read it from the fix path.
 *
 * Slow on purpose. This is for a bug report, not for the alarm, and a bridge call
 * a minute to keep a diagnostic current is already more than it is worth.
 */
export const STATUS_REFRESH_MS = 60_000;

let lastStatusMs = 0;

/** Refresh the cached line if it is due. Safe to call on every fix. */
export async function refreshNativeAnchorLine(nowMs = Date.now()): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (nowMs - lastStatusMs < STATUS_REFRESH_MS) return;
  lastStatusMs = nowMs;
  await nativeAnchorStatus();
}

/** The last route snapshot sent, so an unchanged route is not re-sent. */
let lastRouteJson: string | null = null;

/**
 * Hand the route picture to the native watch, or clear it with null.
 *
 * Sent as the serialised string so what Java parses is byte-for-byte what was
 * built and tested here. Routes change rarely (editing, advancing a leg), so
 * sending on change is cheap; the AIS snapshot below is the one that throttles.
 */
export async function sendRouteWatch(json: string | null): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const payload = json ?? '';
  if (payload === lastRouteJson) return;
  try {
    await AnchorWatchNative.routeWatch({ json: payload });
    lastRouteJson = payload;
  } catch {
    // The web app's own route alarms still run; losing the backup is not worth
    // interrupting anyone over.
  }
}

/**
 * How often the AIS picture is refreshed for the native side.
 *
 * Targets update every few seconds, and writing a file for each would be most
 * of a filesystem's day. Thirty seconds is fine: the native check dead-reckons
 * targets forward from their stamped times, so a snapshot half a minute old
 * costs nothing in accuracy, only in how quickly a brand-new target is seen.
 */
export const AIS_SNAPSHOT_MS = 30_000;

let lastAisSentMs = 0;

/** Send the AIS picture if one is due. Call freely; it throttles itself. */
export async function sendAisSnapshot(
  json: string | null,
  nowMs = Date.now(),
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (json != null && nowMs - lastAisSentMs < AIS_SNAPSHOT_MS) return;
  lastAisSentMs = nowMs;
  try {
    await AnchorWatchNative.aisSnapshot({ json: json ?? '' });
  } catch {
    // Missing a snapshot ages the native picture, and an aged picture makes the
    // native side declare itself blind, which is the safe direction.
  }
}

/**
 * Ask Android whether an alarm raised right now would actually reach the crew.
 *
 * Returns the problem in plain English (an app-level block, or a silenced or
 * muted alarm channel), or null when the road is clear or the question cannot
 * be answered. Asked when a watch arms, because that is the moment the skipper
 * can still do something about it; discovering it at alarm time is a
 * post-mortem, which is exactly what happened on 7 Aug.
 */
export async function checkAlarmDelivery(): Promise<AlarmDeliveryProblem | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const r = await AnchorWatchNative.checkAlarmDelivery();
    if (!r.problem) return null;
    // Two kinds of problem, two different Android screens to fix them in:
    // notifications decide whether an alarm can be HEARD, battery optimisation
    // decides whether anything is left ALIVE to raise it (the 9 Aug drive-off).
    return { problem: r.problem, kind: r.kind === 'battery' ? 'battery' : 'notifications' };
  } catch {
    // An unanswerable question is not a delivery problem.
    return null;
  }
}

/** Pop Android's own "let this app run unrestricted" dialog. */
export async function requestUnrestrictedBattery(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await AnchorWatchNative.requestUnrestrictedBattery();
  } catch {
    // Best-effort; the banner text still names the setting.
  }
}

/** One tap to the app's notification settings, where the fix lives. */
export async function openAlarmSettings(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await AnchorWatchNative.openNotificationSettings();
  } catch {
    // Best-effort; the degrade banner text still names the settings path.
  }
}

/** Test seam: forget what we think the native side has been told. */
export function resetNativeAnchorCache(): void {
  lastSentDisarmed = false;
  lastArmKey = null;
  lastStampMs = 0;
  lastStatusMs = 0;
  cachedLine = null;
  lastRouteJson = null;
  lastAisSentMs = 0;
}

/** Should be true whenever a stamp is due. Pulled out so it can be tested. */
export function stampIsDue(nowMs: number, lastMs: number): boolean {
  return nowMs - lastMs >= ALIVE_STAMP_MS;
}
